import { LOCAL_STORAGE_KEY_JWT } from '@/api'
import { useFetchWorkers } from '@/features/devices/api/useFetchWorkers'
import { useCurrentConfiguration } from '@/features/devices/api/version'
import { routes } from '@/pages'
import { SignUpModal } from '@/pages/SignUpPage'
import { Button, Text } from '@/ui-kit'
import { Dialog, DialogContent, DialogTrigger } from '@/ui-kit/components/Dialog'
import { sendMessageToExtension } from '@/utils'
import { useLocalStorage } from '@/utils/useLocalStorage'
import { useWallet } from '@solana/wallet-adapter-react'
import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { authService } from '../api/authService'
import { useFetchUser } from '../api/useFetchUser'
import { useInviteCode } from './InviteCodeProvider'

type SessionProviderValue = {
  signInWithMagicLink: (email: string) => Promise<void>
  logout: () => Promise<void>
}

const SessionProviderContext = createContext({} as SessionProviderValue)

export const SessionProvider = ({ children }: PropsWithChildren) => {
  const location = useLocation()
  const [showModal, setShowModal] = useState(false)
  const [showSignUpModal, setShowSignUpModal] = useState(false)
  const [jwt, setJwt] = useLocalStorage(LOCAL_STORAGE_KEY_JWT, '')
  const [isReady, setIsReady] = useState(false)
  const { data: user } = useFetchUser(!!jwt)
  const { data: config } = useCurrentConfiguration()
  const { data: workers, isFetched, refetch: refetchWorkers } = useFetchWorkers(false, !!jwt)
  const { disconnect: disconnectWallet } = useWallet()
  const { inviteCode } = useInviteCode()

  const logout = useCallback(async () => {
    try {
      await disconnectWallet()
      await sendMessageToExtension('logout')
      localStorage.removeItem(LOCAL_STORAGE_KEY_JWT)
    } catch (err) {
      console.log('Logout error: ', err)
    } finally {
      setJwt('')
      window.location.replace(routes.login.path)
    }
  }, [])

  useEffect(() => {
    const handleFocus = async () => {
      if (jwt) {
        try {
          const result = await sendMessageToExtension<{ isNewlyInstalled: boolean }>('isInstalled')
          const isNewlyInstalled = result?.isNewlyInstalled
          if (isNewlyInstalled !== undefined) {
            if (isNewlyInstalled) {
              await sendMessageToExtension<void>('login', { email: user?.email || '-', jwt: jwt })
              const webWorkersLength = workers?.filter((worker) => worker.type === 'extension').length
              setShowModal(isFetched && !webWorkersLength)
            } else {
              const isExtensionLoggedIn = await sendMessageToExtension('isLoggedIn')
              if (!isExtensionLoggedIn) {
                logout()
              }
            }
          }
        } catch (error) {
          console.error(error)
        }
      }
    }

    window.addEventListener('focus', handleFocus)
    handleFocus()

    return (): void => window.removeEventListener('focus', handleFocus)
  }, [jwt, workers, logout, user])

  useEffect(() => {
    const isPublicPath = Object.entries(routes).find(
      ([, route]) => route.isPublic && location.pathname.includes(route.path)
    )
    if (!isPublicPath && user && !user.isSignUpCompleted) {
      setShowSignUpModal(true)
    } else if (user?.isSignUpCompleted) {
      setShowSignUpModal(false)
    }
  }, [user, location])

  useEffect(() => {
    async function checkSession() {
      if (jwt) {
        setIsReady(true)
        if (window.location.pathname.includes('/login')) {
          window.location.replace('/')
        }
      } else {
        setIsReady(true)
      }
    }
    checkSession()
  }, [])

  const signInWithMagicLink = useCallback(
    async (email: string) => {
      await authService.requestMagicLink(email, inviteCode)
    },
    [inviteCode]
  )

  const value: SessionProviderValue = useMemo(
    () => ({
      signInWithMagicLink,
      logout
    }),
    [signInWithMagicLink, logout]
  )

  return (
    <SessionProviderContext.Provider value={value}>
      {isReady && children}

      <Dialog open={showModal} onOpenChange={(value) => setShowModal(value)}>
        <DialogTrigger />
        <DialogContent className="!max-w-[520px]">
          <img
            alt=""
            src="/images/firework-orange.png"
            className="absolute top-0 left-1/2 h-[188px] w-[188px] -translate-x-3/4 -translate-y-[80%]"
          />
          <div className="absolute top-0 left-1/3 h-[60px] w-1/2 bg-[var(--colorBlackToWhite)]" />
          <img
            alt=""
            src="/images/firework-green.png"
            className="absolute top-0 left-[47%] h-[188px] w-[188px] -translate-y-3/4"
          />
          <div className="mt-6 flex flex-col items-center">
            <Text type="h3" className="mb-2">
              Congratulations!
            </Text>
            <span className="max-w-[320px] text-center">
              You've earned
              <span className="text-[var(--color)]">{` ${config?.extension_installation_reward_points} points`}</span>{' '}
              for completing your first browser extension installation!
            </span>
          </div>
          <Button
            className="mt-4"
            onClick={() => {
              setShowModal(false)
              refetchWorkers()
            }}
          >
            Start earning
          </Button>
        </DialogContent>
      </Dialog>
      <SignUpModal refCode={inviteCode || ''} open={showSignUpModal} setIsOpen={setShowSignUpModal} />
    </SessionProviderContext.Provider>
  )
}

export function useSessionProvider(): SessionProviderValue {
  return useContext(SessionProviderContext)
}
